import queryString from 'query-string'
import dayjs from 'dayjs'
import { saveAs } from 'file-saver'
import Router from 'next/router'
import { ORDER_CURRENT_STATUS_CODE } from 'constants/order-constants/order'
import OrderApi from 'lib/api/order'
import PaymentApi from 'lib/api/payment'
import {
  getOrderIDFromReference,
  returnOrderPrefix,
} from 'lib/utils/common/commonUtils'
import { segmentConfirmExtendClicked } from 'lib/segment'
import StateStorage from 'lib/state-storage'
import { getFileSizeMB } from 'lib/utils/images/compression'
import { MOBILE_BANKING_TYPE } from 'components/checkout/mobile-banking-list/const'
import { MANUAL_BANK_PAY_INFO, MAX_FILE_SIZE } from './const'

// Statuses where payment has not been confirmed as successful yet.
// "Order Completed" must not fire for these when returning from a
// payment gateway redirect (e.g. Omise/Adyen/ABA card payment).
const NON_COMPLETED_ORDER_STATUS_CODES = [
  ORDER_CURRENT_STATUS_CODE.PAYMENT_ERROR,
  ORDER_CURRENT_STATUS_CODE.AWAITING_BANK_WIRE_PAYMENT,
  ORDER_CURRENT_STATUS_CODE.AWAITING_PAYPAL_PAYMENT,
  ORDER_CURRENT_STATUS_CODE.PENDING,
  ORDER_CURRENT_STATUS_CODE.AWAITING_PAYMENT,
  ORDER_CURRENT_STATUS_CODE.CANCELED,
  ORDER_CURRENT_STATUS_CODE.BANK_WIRE_FAILED,
  ORDER_CURRENT_STATUS_CODE.WMS_CANCELED,
  ORDER_CURRENT_STATUS_CODE.DELIVERY_FAILED,
  ORDER_CURRENT_STATUS_CODE.MP_CANCELED,
]

const getOrderCompletedTrackedKey = (idOrder) =>
  `segment-order-completed-tracked-${idOrder}`

// Marks an order as needing "Order Completed" tracking once its full
// details have loaded. Called when returning from a payment gateway
// redirect (?id_order=X), right before the URL gets rewritten to the
// reference-based path via window.location.replace() -- that navigation
// would otherwise abort an in-flight fetch+track call before the event's
// real data is ready, so the flag is picked up post-reload instead, once
// the order page has already fetched full order details for its own use.
const markOrderForCompletionTracking = (idOrder) => {
  const trackedKey = getOrderCompletedTrackedKey(idOrder)
  if (sessionStorage.getItem(trackedKey)) return

  sessionStorage.setItem(trackedKey, 'pending')
}

const ORDER_STATES = {
  PAYMENT_ACCEPTED: 2,
  PAYMENT_ERROR: 8,
  AWAITING_BANK_WIRE_PAYMENT: 10,
  PENDING: 13,
  BANK_WIRE_ACCEPTED: 22,
  BANK_WIRE_FAILED: 23,
  OUT_FOR_DELIVERY: 25,
  PAYMENT_PENDING: 37,
}

const downloadInvoice = (e, order) => {
  e?.preventDefault()
  OrderApi.getInvoice(order.invoice_number).then((blob) =>
    saveAs(blob, `${order.reference}.pdf`),
  )
}

const getBankPaymentInfo = (hasProof, isPast) => {
  const awaitPayment = !isPast && !hasProof && MANUAL_BANK_PAY_INFO.awaitPayment
  const gotProof = hasProof && MANUAL_BANK_PAY_INFO.gotProof
  const pastDue = isPast && MANUAL_BANK_PAY_INFO.pastDue

  return awaitPayment || gotProof || pastDue
}

const getEndDate = (order) =>
  order?.payment?.expiration_proof_date &&
  !order?.payment?.account?.proof_upload
    ? order.payment.expiration_proof_date
    : undefined

const getExtendablityStatus = (isExtendOptionHidden, isExtended, order) => {
  const isExtendableShippingType = ['store', 'pickup'].includes(
    order?.shipping_type,
  )
  const isOutForDelivery =
    order?.current_status_code === ORDER_CURRENT_STATUS_CODE.OUT_FOR_DELIVERY
  const isPomeloEmployeeOrder = order?.pickup_location_type !== 'OFFICE'

  return (
    !isExtendOptionHidden &&
    !isExtended &&
    isExtendableShippingType &&
    isOutForDelivery &&
    isPomeloEmployeeOrder
  )
}

const getFormattedDate = (order) => {
  const newExpiryDate = dayjs(order.expiry_date, 'YYYY-MM-DD').add(
    order.available_extension_days,
    'd',
  )
  return newExpiryDate.format('DD MMM YYYY')
}

const replaceOrderIdWithReference = (id_order) => {
  const orderReference = returnOrderPrefix(id_order) + id_order
  const pathPrefix = window.location.pathname

  window.location.replace(`${pathPrefix}${orderReference}`)
}

const getOrderId = (id, reference, updateId) => {
  const urlParams = queryString.parse(window.location.search)

  if (urlParams?.id_order) {
    // Returning from a payment gateway redirect (Omise/Adyen/ABA card
    // payment) lands here with ?id_order=X before the URL below gets
    // rewritten to the reference-based path, losing that signal.
    markOrderForCompletionTracking(urlParams.id_order)
    replaceOrderIdWithReference(urlParams?.id_order)
  }

  if (id) {
    return id
  }

  const orderId = getOrderIDFromReference(reference)

  if (id !== orderId) {
    updateId(orderId)
  }

  return orderId
}

const getOrderIDReference = (reference) =>
  parseInt(reference.replace('PML79', ''), 10)

const handleExtendClickAPI = async (
  id,
  updateExtendSuccess,
  updateExtendErrorData,
  user,
) => {
  segmentConfirmExtendClicked({
    id_customer: user?.id_customer,
    ref_type: 'order_details',
  })

  try {
    await OrderApi.extendPickupDate({
      orderId: id,
    })

    updateExtendSuccess(true)
  } catch (err) {
    updateExtendErrorData(err.error.message)
  }
}

const isManualBankPayment = (order) =>
  typeof order?.payment?.type === 'string' &&
  order.payment.type.startsWith('Manual Bank Transfer')

const isPickUpCompleted = (order) =>
  [
    ORDER_CURRENT_STATUS_CODE.SUCCESSFUL_PICK_UP,
    ORDER_CURRENT_STATUS_CODE.DELIVERED,
  ].includes(order.current_status_code)

const isVoucherWithTTBS = (order) => {
  const isTTBS = order?.pickup_location_type?.toLowerCase() === 'store'
  const isVoucherApplied = order?.voucher
  return isTTBS && isVoucherApplied
}

const onDrop = (files, updateImageUploadData) => {
  if (files?.length) {
    const fileSizeMB = getFileSizeMB(files[0])

    if (fileSizeMB > MAX_FILE_SIZE) {
      updateImageUploadData(
        `File size exceeds the maximum size of ${MAX_FILE_SIZE} MB`,
        null,
        false,
      )
    } else {
      updateImageUploadData(null, files[0], true)
    }
  }
}

const proceedToPay = (e, orderId) => {
  e?.preventDefault()
  const shop = StateStorage.getShop()
  const language = StateStorage.getLanguage()

  const URL = {
    as: `/${shop}/${language}/checkout?id=${orderId}`,
    href: `/checkout?id=${orderId}`,
  }

  Router.push(URL.href, URL.as)
}

// This is the work around with state and re-rendering
const setMaskWidthHeight = (imageEl, previewImageElRef) => {
  const maskEl = document.getElementById('proof_mask')

  if (maskEl) {
    maskEl.style.width = `${imageEl.width}px`
    maskEl.style.height = `${imageEl.height}px`
  }

  previewImageElRef.current.scrollIntoView({ behavior: 'smooth' })
}

const showUploadProof = (hasProof, isPast, order) =>
  order?.current_status_code === 10 && !hasProof && !isPast

const submitPaymentProof = (reference, image, updateProofUploadData) => {
  window.scrollTo(0, 0)
  updateProofUploadData(true)

  PaymentApi.uploadPaymentProof(getOrderIDReference(reference), {
    file: image,
  })
    .then(
      (res) => {
        if ((res.status > 199 && res.status < 300) || res.status === 413) {
          updateProofUploadData(false, true)
        }
      },
      // User already submitted the proof or other request error
      () => {
        updateProofUploadData(false, true)
      },
    )
    .catch(
      /* istanbul ignore next */
      (err) => {
        throw new Error(err)
      },
    )
}

const isMobileBankingPendingOrder = (order) =>
  [ORDER_STATES.PENDING, ORDER_STATES.PAYMENT_PENDING].includes(
    order.current_status_code,
  ) && MOBILE_BANKING_TYPE.includes(order.payment.type_name)

export {
  downloadInvoice,
  getBankPaymentInfo,
  getEndDate,
  getExtendablityStatus,
  getFormattedDate,
  getOrderCompletedTrackedKey,
  getOrderId,
  handleExtendClickAPI,
  isManualBankPayment,
  isMobileBankingPendingOrder,
  isPickUpCompleted,
  isVoucherWithTTBS,
  NON_COMPLETED_ORDER_STATUS_CODES,
  onDrop,
  proceedToPay,
  setMaskWidthHeight,
  showUploadProof,
  submitPaymentProof,
  ORDER_STATES,
}
